/**
 * The trade list (2026-09-29 owner ask): a round "add to trade list" button
 * on a My Pokémon row puts that record on a list the Search page shows.
 *
 * Record ids only, on this device (localStorage). The list resolves ids
 * against the My Pokémon store when drawn, so a record that was deleted
 * simply stops appearing. First cut: the owner wants to build this out.
 */
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "nexus.tradeList.v1";
export const TRADE_LIST_EVENT = "nexus:trade-list-changed";

let cache: readonly string[] | null = null;

function read(): readonly string[] {
  if (cache) return cache;
  if (typeof window === "undefined") return (cache = []);
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    cache = Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    cache = [];
  }
  return cache;
}

function write(ids: readonly string[]): void {
  cache = ids;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* the list lives in memory for the session; nothing else breaks. */
  }
  window.dispatchEvent(new CustomEvent(TRADE_LIST_EVENT));
}

/** Record ids on the list, oldest first. */
export function readTradeList(): readonly string[] {
  return read();
}

export function isOnTradeList(id: string): boolean {
  return read().includes(id);
}

/** Add or remove; returns whether the record is on the list afterwards. */
export function toggleTradeList(id: string): boolean {
  const ids = read();
  if (ids.includes(id)) {
    write(ids.filter((x) => x !== id));
    return false;
  }
  write([...ids, id]);
  return true;
}

/**
 * Put a record at the TOP of the list (2026-10-12 owner ask: the record
 * page's "Add to trades" lands the Pokémon first on the friend's trade box,
 * not at the bottom of whatever was listed before). Already listed: it moves
 * up rather than doubling.
 */
export function addToTradeListFront(id: string): void {
  const ids = read();
  write([id, ...ids.filter((x) => x !== id)]);
}

export function removeFromTradeList(id: string): void {
  const ids = read();
  if (ids.includes(id)) write(ids.filter((x) => x !== id));
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(TRADE_LIST_EVENT, cb);
  return () => window.removeEventListener(TRADE_LIST_EVENT, cb);
}

/** The list, live: re-renders when a row's button changes it. */
export function useTradeList(): readonly string[] {
  return useSyncExternalStore(subscribe, read, () => []);
}

/** Test-only: forget the list. */
export function __resetTradeListForTests(): void {
  cache = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do. */
  }
}
